import { PencilIcon } from '@heroicons/react/outline';
import { Button, Title, Toast } from '@yoast/ui-library';
import cx from 'classnames';
import { Link } from 'react-router-dom';

import ReactDOM from 'react-dom';
import { useGetAnalysis, useGetBrands } from '../../api/generated/insights';
import { AnalysisStatus } from '../../api/generated/insights.schemas';
import RouteGuardLoading from '../../components/RouteGuards/RouteGuardLoading';
import { useBrandAnalysisData } from '../../contexts';
import { isSpecificAnalysisId } from '../../utils/isSpecificAnalysisId';
import Welcome from '../Setup/0-Welcome';
import BrandSentimentCard from './BrandSentimentCard';
import CitationsCard from './CitationsCard';
import CompetitorRankingCard from './CompetitorRankingCard';
import CompetitorSentimentCard from './CompetitorSentimentCard';
import HeaderCard from './HeaderCard';
import MentionsCard from './MentionsCard';
import MentionsProgressCard from './MentionsProgressCard';
import VisibilityScore from './VisibilityScore';

import { ExternalLinkIcon, XIcon } from '@heroicons/react/solid';
import { useEffect, useRef, useState, type PropsWithChildren } from 'react';
import YoastIconSmall from '../../assets/Yoast-icon-small.svg';
import NoResultsCard from './NoResultsCard';

const BrandAnalysis = () => {
  const { brandId, analysisId } = useBrandAnalysisData();

  const { data: brands, isLoading: isLoadingBrands } = useGetBrands({
    query: {
      select: ({ data }) => data?.brands,
    },
  });

  const { data: analysisData, isLoading: isLoadingAnalysis } = useGetAnalysis(
    brandId,
    analysisId,
    {
      query: {
        // Only enable when we have a brand and a concrete analysis id (not 'latest')
        enabled: !!brandId && isSpecificAnalysisId(analysisId),
        select: ({ data }) => data?.analysis,
      },
    },
  );

  const status = analysisData?.status;

  const isDataLoading = isLoadingBrands || isLoadingAnalysis;

  // Check if we should show the main analysis interface
  const shouldShowInterface = brands && brands?.length > 0;

  // Check if we should show the analysis cards
  // For 'latest', we should show cards if we have completed analysis data
  // For specific IDs, we need both a valid ID and completed data
  const shouldShowCards =
    !isDataLoading && analysisData && status === AnalysisStatus.completed;

  const [isToastVisible, setIsToastVisible] = useState(false);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);
  const prevShouldShowCardsRef = useRef<boolean | undefined>(undefined);

  // Check if user has already seen the congratulations toast
  const hasSeenToast =
    localStorage.getItem('yst_congratulations_toast_seen') === 'true';

  useEffect(() => {
    // Only start timer when shouldShowCards transitions from false to true and user hasn't seen toast
    if (shouldShowCards && !prevShouldShowCardsRef.current && !hasSeenToast) {
      toastTimerRef.current = setTimeout(() => {
        setIsToastVisible(true);
        // Mark toast as seen in localStorage
        localStorage.setItem('yst_congratulations_toast_seen', 'true');
      }, 10000);
    }

    // Only reset if shouldShowCards transitions from true to false
    if (!shouldShowCards && prevShouldShowCardsRef.current) {
      setIsToastVisible(false);
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    }

    prevShouldShowCardsRef.current = shouldShowCards;

    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [shouldShowCards, hasSeenToast]);

  function ToastPortal({ children }: PropsWithChildren) {
    if (typeof window === 'undefined') {
      return null;
    }
    // Render into tsx-toasts container which is a child of toast-portal
    // This allows both toast systems to stack vertically in the same space
    const toastRoot = document.getElementById('tsx-toasts');
    return toastRoot ? ReactDOM.createPortal(children, toastRoot) : null;
  }

  const SurveyToast = ({
    isVisible,
    setIsVisible,
  }: {
    isVisible: boolean;
    setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    const dismissToast = () => {
      setIsVisible(false);
    };

    return (
      <Toast
        id="toast"
        isVisible={isVisible}
        setIsVisible={(visible: boolean) => {
          // Only allow the Toast to hide itself, not show itself
          // We control showing through our timer logic
          if (!visible) {
            setIsVisible(false);
          }
        }}
      >
        <div className="yst-mb-3 yst-flex yst-items-start yst-gap-3">
          <img src={YoastIconSmall} />
          <div className="yst-grow">
            <Title size="3" className="yst-mb-1">
              Congratulations on your first analysis!
            </Title>
            You’ve completed your brand analysis. We’d love to hear what you
            think.
          </div>
          <Button variant="tertiary" onClick={dismissToast}>
            <XIcon />
          </Button>
        </div>
        <div className="yst-flex yst-justify-end yst-gap-4">
          <Button variant="tertiary" onClick={dismissToast}>
            Dismiss
          </Button>
          <Button
            as="a"
            href="https://www.surveymonkey.com/r/HGSB8DL"
            target="_blank"
          >
            Give feedback
            <ExternalLinkIcon className="yst-ml-2" />
          </Button>
        </div>
      </Toast>
    );
  };

  // Prevent flashing the loading state if we don't know whether we are in setup or analysis yet
  if (isLoadingBrands) return <RouteGuardLoading />;

  return shouldShowInterface ? (
    <div className="yst-space-y-4">
      {/* Header card provides the dropdowns to select brand,
				model and analysis */}
      <HeaderCard />

      {/* Analysis content - widgets, etc. - Only show when we
				have completed analysis */}
      {shouldShowCards && (
        <div className="yst-space-y-4">
          <div className="yst-flex yst-items-center yst-justify-between">
            <Title size="2">Your brand analysis</Title>
            <Button
              as={Link}
              variant="secondary"
              to={`/brands/${brandId}/brand-list`}
              className={cx('yst-gap-1.5')}
              disabled={!brandId}
            >
              <PencilIcon /> Edit brand list
            </Button>
          </div>

          <div className="yst-grid yst-grid-cols-1 yst-gap-4 md:yst-grid-cols-2">
            <NoResultsCard />
            <VisibilityScore />
            <MentionsCard />
            <MentionsProgressCard />
            <CompetitorRankingCard />
            <CitationsCard />
            <BrandSentimentCard />
            <CompetitorSentimentCard />
          </div>
          <ToastPortal>
            <SurveyToast
              isVisible={isToastVisible}
              setIsVisible={setIsToastVisible}
            />
          </ToastPortal>
        </div>
      )}
    </div>
  ) : (
    <Welcome />
  );
};

export default BrandAnalysis;
